import { REACTIONS, type ClientView, type Verdict } from '@gazette/shared';
import { locale, t } from '../../i18n/index.ts';
import { actions, playerById } from '../../net/client.ts';
import { ErrorNotice, GameHeader } from '../components/Chrome.tsx';
import { Clipping } from '../components/Clipping.tsx';
import { Portrait } from '../components/Portrait.tsx';
import { Progress } from '../components/Progress.tsx';
import type { PhaseOf } from '../hooks.ts';

/** One folder tab per rumor: where the investigation stands, and how each case closed. */
export function CaseTabs({ index, total, verdicts, articleIds }: { index: number; total: number; verdicts: Verdict[]; articleIds: string[] }) {
  return (
    <ol class="case-tabs" aria-label={t('inv.case', { n: index + 1, total })}>
      {Array.from({ length: total }, (_, position) => {
        const verdict = verdicts.find((candidate) => candidate.articleId === articleIds[position]);
        const state = position === index ? 'current' : verdict ? 'closed' : 'open';
        return (
          <li class={`case-tab case-tab--${state}`} aria-current={position === index ? 'step' : undefined}>
            <span>{position + 1}</span>
            {verdict && position !== index && <span class="case-tab__mark">{t(verdict.isTrue ? 'rev.true' : 'rev.false').charAt(0)}</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function Investigation({ current, phase }: { current: ClientView; phase: PhaseOf<'investigation'> }) {
  const article = current.gazette!.articles.find((candidate) => candidate.id === phase.articleId)!;
  const victim = playerById(article.targetId);
  const suspects = current.players.filter((player) => player.id !== current.meId && player.id !== article.targetId);
  const vote = phase.vote;

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--case" data-occlude aria-labelledby="case-title">
        <CaseTabs index={phase.index} total={phase.total} verdicts={current.verdicts} articleIds={current.gazette!.articles.map((entry) => entry.id)} />
        <h2 id="case-title" class="visually-hidden">
          {t('inv.case', { n: phase.index + 1, total: phase.total })}
        </h2>
        <Clipping article={article} victim={victim} />

        {phase.victimReaction && victim && (
          <blockquote class="reaction">
            <p class="reaction__quote">
              {locale.value === 'fr' ? `«\u00A0${t(`reaction.${phase.victimReaction}`)}\u00A0»` : `“${t(`reaction.${phase.victimReaction}`)}”`}{' '}
              <span class="reaction__who">{t('inv.reaction', { name: victim.name })}</span>
            </p>
          </blockquote>
        )}

        {phase.role === 'victim' ? (
          <div class="victim-panel">
            <h3 class="victim-panel__title">{t('inv.victim.title')}</h3>
            <p>{t('inv.victim.body')}</p>
            <div class="reactions" role="group" aria-label={t('inv.victim.react')}>
              <span class="field__label">{t('inv.victim.react')}</span>
              <div class="reactions__list">
                {REACTIONS.map((reaction) => (
                  <button type="button" class="chip chip--dark" aria-pressed={phase.victimReaction === reaction} onClick={() => void actions.react(article.id, reaction)}>
                    {t(`reaction.${reaction}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {phase.role === 'author' && <p class="author-note">{t('inv.author.note')}</p>}
            <fieldset class="versus">
              <legend class="section-title">{t('inv.truth')}</legend>
              <div class="versus__halves">
                <button type="button" class="versus__half" aria-pressed={vote.truth === true} onClick={() => void actions.vote(article.id, { truth: true })}>
                  {t('inv.true')}
                </button>
                <span class="versus__mark" aria-hidden="true">
                  {t('inv.or')}
                </span>
                <button type="button" class="versus__half" aria-pressed={vote.truth === false} onClick={() => void actions.vote(article.id, { truth: false })}>
                  {t('inv.false')}
                </button>
              </div>
            </fieldset>
            <fieldset class="accuse">
              <legend class="section-title">{t('inv.author')}</legend>
              <div class="accuse__grid">
                {suspects.map((player) => (
                  <button
                    type="button"
                    class="suspect-card"
                    aria-pressed={vote.authorGuess === player.id}
                    aria-label={t('inv.accuse', { name: player.name })}
                    onClick={() => void actions.vote(article.id, { authorGuess: player.id })}
                  >
                    <Portrait seed={player.portrait} width={54} />
                    <span class="suspect-card__name">{player.name}</span>
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}
        <ErrorNotice />
        <Progress progress={phase.progress} label={t('progress.voted', phase.progress)} />
      </section>
    </>
  );
}
